import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  BASLIK: "",
  TIP: "Makale",
  VIDEO_URL: "",
  Blog_Image: "",
  Blog_Desc: "",
  SURE: "",
  ILGILI_KATEGORI_AD: "",
  YAYIN_TARIHI: "",
  YAZAR: "",
  OKUMA_SURESI: "",
};

const BlogYonetimi = () => {
  document.title = "Blog Yönetimi | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [bloglar, setBloglar] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchBloglar = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/BLOG?$orderby=ID desc`);
      const result = await res.json();
      setBloglar(result.value || []);
    } catch {
      setError("Blog yazıları yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchBloglar(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (blog) => {
    setFormData({
      ID: blog.ID,
      BASLIK: blog.BASLIK || "",
      TIP: blog.TIP || "Makale",
      VIDEO_URL: blog.VIDEO_URL || "",
      Blog_Image: blog.Blog_Image || "",
      Blog_Desc: blog.Blog_Desc || "",
      SURE: blog.SURE || "",
      ILGILI_KATEGORI_AD: blog.ILGILI_KATEGORI_AD || "",
      YAYIN_TARIHI: blog.YAYIN_TARIHI ? blog.YAYIN_TARIHI.substring(0, 10) : "",
      YAZAR: blog.YAZAR || "",
      OKUMA_SURESI: blog.OKUMA_SURESI ?? "",
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.BASLIK.trim()) {
      setError("Başlık alanı zorunludur.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        BASLIK: formData.BASLIK,
        TIP: formData.TIP,
        YAZAR: formData.YAZAR,
        Blog_Desc: formData.Blog_Desc,
        Blog_Image: formData.Blog_Image,
        VIDEO_URL: formData.VIDEO_URL || null,
        SURE: formData.SURE || null,
        ILGILI_KATEGORI_AD: formData.ILGILI_KATEGORI_AD || null,
        YAYIN_TARIHI: formData.YAYIN_TARIHI || null,
        OKUMA_SURESI: formData.OKUMA_SURESI ? Number(formData.OKUMA_SURESI) : null,
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/BLOG(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Blog yazısı güncellendi.");
      } else {
        await fetch(`${BASE_URL}/BLOG`, {
          method: "POST", headers,
          body: JSON.stringify({ ...payload, ID: Math.floor(Date.now() / 1000) }),
        });
        setSuccessMsg("Blog yazısı eklendi.");
      }

      setModal(false);
      fetchBloglar();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu blog yazısını silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/BLOG(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Blog yazısı silindi.");
      fetchBloglar();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Görsel",
      accessorKey: "Blog_Image",
      enableSorting: false,
      cell: (c) => {
        const url = c.getValue();
        return url
          ? <img src={url} alt="blog" style={{ height: 44, width: 70, objectFit: "cover", borderRadius: 4 }} onError={(e) => { e.target.style.display = "none"; }} />
          : <span className="text-muted small">Görsel yok</span>;
      },
    },
    {
      header: "Başlık",
      accessorKey: "BASLIK",
      enableSorting: true,
      cell: (c) => (
        <span className="fw-medium" style={{ maxWidth: 220, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {c.getValue()}
        </span>
      ),
    },
    {
      header: "Tip",
      accessorKey: "TIP",
      enableSorting: true,
      cell: (c) => <Badge color="info" pill>{c.getValue()}</Badge>,
    },
    {
      header: "Yazar",
      accessorKey: "YAZAR",
      enableSorting: true,
      cell: (c) => c.getValue() || <span className="text-muted">---</span>,
    },
    {
      header: "Yayın Tarihi",
      accessorKey: "YAYIN_TARIHI",
      enableSorting: true,
      cell: (c) => c.getValue()
        ? new Date(c.getValue()).toLocaleDateString("tr-TR")
        : <span className="text-muted">---</span>,
    },
    {
      header: "Görüntülenme",
      accessorKey: "GÖRÜNTÜLENME_SAYISI",
      enableSorting: true,
      cell: (c) => <Badge color="light" className="text-dark">{c.getValue() ?? 0}</Badge>,
    },
    {
      header: "Beğeni",
      accessorKey: "BEGENI_SAYISI",
      enableSorting: true,
      cell: (c) => <Badge color="danger" pill>{c.getValue() ?? 0}</Badge>,
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const b = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(b)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(b.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İçerik Yönetimi" breadcrumbItem="Blog Yönetimi" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Blog Yazıları</h4>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Blog Yazısı
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={bloglar}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={10}
                      SearchPlaceholder="Blog yazısı ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-pencil" : "mdi-plus-circle"} me-2 text-primary`}></i>
          {isEditing ? `Blog Düzenle (#${formData.ID})` : "Yeni Blog Yazısı"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={8}>
              <FormGroup>
                <Label className="fw-bold">Başlık <span className="text-danger">*</span></Label>
                <Input value={formData.BASLIK} onChange={(e) => f("BASLIK", e.target.value)} placeholder="Blog başlığı" />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">Tip</Label>
                <Input type="select" value={formData.TIP} onChange={(e) => f("TIP", e.target.value)}>
                  <option>Makale</option>
                  <option>Video</option>
                  <option>Haber</option>
                  <option>Duyuru</option>
                  <option>Rehber</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Yazar</Label>
                <Input value={formData.YAZAR} onChange={(e) => f("YAZAR", e.target.value)} placeholder="Yazar adı" />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label className="fw-bold">Yayın Tarihi</Label>
                <Input type="date" value={formData.YAYIN_TARIHI} onChange={(e) => f("YAYIN_TARIHI", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label className="fw-bold">Okuma Süresi (dk)</Label>
                <Input type="number" min="1" value={formData.OKUMA_SURESI} onChange={(e) => f("OKUMA_SURESI", e.target.value)} placeholder="5" />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Görsel URL</Label>
                <Input value={formData.Blog_Image} onChange={(e) => f("Blog_Image", e.target.value)} placeholder="https://..." />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Video URL</Label>
                <Input value={formData.VIDEO_URL} onChange={(e) => f("VIDEO_URL", e.target.value)} placeholder="https://youtube.com/..." />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">İlgili Kategori</Label>
                <Input value={formData.ILGILI_KATEGORI_AD} onChange={(e) => f("ILGILI_KATEGORI_AD", e.target.value)} placeholder="Örn: Tarım" />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Süre (metin)</Label>
                <Input value={formData.SURE} onChange={(e) => f("SURE", e.target.value)} placeholder="Örn: 5dk" />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">İçerik / Açıklama</Label>
                <Input
                  type="textarea" rows={6}
                  value={formData.Blog_Desc}
                  onChange={(e) => f("Blog_Desc", e.target.value)}
                  placeholder="Blog içeriği..."
                />
              </FormGroup>
            </Col>
            {formData.Blog_Image && (
              <Col md={12}>
                <Label className="fw-bold">Görsel Önizleme</Label>
                <img
                  src={formData.Blog_Image} alt="önizleme"
                  className="img-fluid rounded border"
                  style={{ maxHeight: 140, objectFit: "cover" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </Col>
            )}
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default BlogYonetimi;
